import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    ImageIcon, Plus, Loader2, X, Trash2, Search, Upload,
    Pencil, RotateCcw, History, Check, Link2 as LinkIcon,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useSizing } from "@/lib/sizing-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { PageHeader, EmptyState, PrimaryButton, DeleteConfirmDialog } from "@/components/design-system";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const MAX_BYTES = 3 * 1024 * 1024;  // 3MB — backend ile senkron

/** Public serve URL. `v=` busts CDN + browser cache whenever the merchant
 * uploads a new version or rolls back to an older one — otherwise linked
 * products would keep showing the stale bytes. */
const imageSrc = (id, version) => {
    const v = version || 1;
    return `${BACKEND}/api/embed/images/${id}?v=${v}`;
};

/** Format bytes for display (KB / MB). */
const formatSize = (b) => {
    if (b == null) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

/** Extract short mime label — "image/png" → "PNG". */
const mimeLabel = (mime) => (mime || "").split("/")[1]?.toUpperCase().replace("SVG+XML", "SVG") || "IMAGE";

/** Format ISO date short */
const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return ""; }
};

/**
 * Sayz görsel yükleme stratejisi — SERBEST + AKILLI ETİKET (BACKEND).
 * Merchant istediği oranda görsel yükler; oran/konum zorlaması YOK.
 * Backend Pillow ile w×h ölçüp `orientation: portrait | landscape | square`
 * etiketler. Bu etiket UI'da GÖSTERİLMEZ — sadece widget config fazında
 * modal içi yerleşim kararı için arka planda tutulur. Merchant'a tek soft
 * uyarı: min 600px genişlik (widget'ta retina bulanıklık için).
 */
const MIN_RECOMMENDED_WIDTH = 600;
const SQUARE_TOLERANCE = 0.05;

const classifyOrientation = (w, h) => {
    if (!w || !h) return null;
    const ratio = w / h;
    if (Math.abs(ratio - 1) <= SQUARE_TOLERANCE) return "square";
    return ratio > 1 ? "landscape" : "portrait";
};

/** Client-side dimension probe — raster via <img> naturalWidth, SVG via
 * regex on file text (width/height or viewBox). Always resolves; never
 * throws — upload should still proceed if probing fails. */
const probeDimensions = (file) => new Promise((resolve) => {
    if (file.type === "image/svg+xml") {
        const reader = new FileReader();
        reader.onload = () => {
            const text = String(reader.result || "").slice(0, 2048);
            let m = text.match(/<svg[^>]*?width=["']?([\d.]+)[a-z%]*["']?[^>]*?height=["']?([\d.]+)/i);
            if (!m) m = text.match(/<svg[^>]*?height=["']?([\d.]+)[a-z%]*["']?[^>]*?width=["']?([\d.]+)/i);
            if (m) return resolve({ width: parseInt(m[1], 10), height: parseInt(m[2], 10) });
            const vb = text.match(/viewBox=["']?\s*[\d.\-]+\s+[\d.\-]+\s+([\d.]+)\s+([\d.]+)/i);
            if (vb) return resolve({ width: parseInt(vb[1], 10), height: parseInt(vb[2], 10) });
            resolve({ width: null, height: null });
        };
        reader.onerror = () => resolve({ width: null, height: null });
        reader.readAsText(file);
        return;
    }
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: null, height: null });
    };
    img.src = url;
});

/**
 * YAPILANDIRMA > GÖRSELLER
 * DAM (Digital Asset Management) — measurement diagram library.
 * ID-based linking: renaming does NOT break product associations.
 * VERSIONING: each image tracks its own history. Uploading a new file adds
 * a new version; the old bytes stay in `history` for rollback. All products
 * referencing `sizingImageId=<id>` instantly see the current version.
 */
export default function ImagesLibrary() {
    const { images, products, loaded, uploadImage, renameImage, uploadImageVersion, rollbackImageVersion, removeImage } = useSizing();
    const [uploadOpen, setUploadOpen] = useState(false);
    const [detailImage, setDetailImage] = useState(null);
    const [search, setSearch] = useState("");
    const [deleteImage, setDeleteImage] = useState(null);

    // Re-hydrate the detail sheet's image whenever the store's images list
    // updates (after uploadImageVersion/rollback) so the version list refreshes
    // without closing the sheet.
    const detailFresh = useMemo(() => (
        detailImage ? images.find((i) => i.id === detailImage.id) || null : null
    ), [detailImage, images]);

    const filtered = useMemo(() => {
        if (!search.trim()) return images;
        const q = search.trim().toLowerCase();
        return images.filter((img) => (img.name || "").toLowerCase().includes(q));
    }, [images, search]);

    return (
        <div className="space-y-6" data-testid="images-library-page">
            <PageHeader
                icon={ImageIcon}
                title="Görseller"
                description="Beden tablosu görselleri kütüphanesi. Bir görseli bir kere yükle, ürünlere bağla; güncelleyince tüm ürünlerde otomatik değişir."
                actions={
                    <PrimaryButton onClick={() => setUploadOpen(true)} data-testid="images-add-btn">
                        <Plus className="h-3.5 w-3.5" /> Yeni Görsel Ekle
                    </PrimaryButton>
                }
            />

            {/* Search — takes full width; the count is folded into the
                placeholder + a subtle "N sonuç" hint under the input when
                the merchant is actively filtering. No more awkward chip
                sitting next to the search box. */}
            {loaded && images.length > 0 && (
                <div className="space-y-1.5">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={`${images.length} görsel içinde ara...`}
                            className="pl-9"
                            data-testid="images-search"
                        />
                    </div>
                    {search.trim() && (
                        <div className="text-[11px] text-muted-foreground pl-1" data-testid="images-search-result-count">
                            <span className="font-mono num text-foreground">{filtered.length}</span> sonuç bulundu
                        </div>
                    )}
                </div>
            )}

            {/* Loading */}
            {!loaded && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-6" data-testid="images-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…
                </div>
            )}

            {/* Empty state */}
            {loaded && images.length === 0 && (
                <EmptyState
                    icon={ImageIcon}
                    title="Henüz Görsel Yok"
                    description="Beden tablolarında kullanmak için ölçüm diyagramı, silüet çizim veya nasıl-ölçülür rehberi ekleyin."
                    action={{
                        label: "İlk Görseli Ekle",
                        onClick: () => setUploadOpen(true),
                        icon: Plus,
                        testId: "images-empty-add-btn",
                    }}
                    testId="images-empty-state"
                />
            )}

            {/* Filter empty state */}
            {loaded && images.length > 0 && filtered.length === 0 && (
                <div className="rounded-xl border border-border bg-muted/20 p-8 text-center text-sm text-muted-foreground" data-testid="images-filter-empty">
                    &quot;{search}&quot; ile eşleşen görsel bulunamadı.
                </div>
            )}

            {/* Grid of image cards — Models sayfasıyla aynı DNA */}
            {loaded && filtered.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="images-grid">
                    {filtered.map((img) => (
                        <ImageCard
                            key={img.id}
                            img={img}
                            onOpen={() => setDetailImage(img)}
                            onDelete={() => setDeleteImage(img)}
                        />
                    ))}
                </div>
            )}

            {/* Upload sheet — brand-new image */}
            <UploadSheet
                open={uploadOpen}
                onClose={() => setUploadOpen(false)}
                onUpload={uploadImage}
            />

            {/* Detail sheet — edit metadata + upload new version + rollback */}
            <ImageDetailSheet
                image={detailFresh}
                onClose={() => setDetailImage(null)}
                onRename={renameImage}
                onUploadVersion={uploadImageVersion}
                onRollback={rollbackImageVersion}
                onDelete={(img) => { setDetailImage(null); setDeleteImage(img); }}
            />

            {/* Delete confirm with product cascade warning */}
            <ImagesDeleteDialog
                image={deleteImage}
                products={products}
                onCancel={() => setDeleteImage(null)}
                onConfirm={async () => {
                    const target = deleteImage;
                    setDeleteImage(null);
                    try {
                        const res = await removeImage(target.id);
                        const msg = res.unlinkedProducts > 0
                            ? `Görsel silindi. ${res.unlinkedProducts} ürün görselsiz kaldı.`
                            : "Görsel silindi";
                        toast.success(msg);
                    } catch (e) {
                        toast.error(e?.response?.data?.detail || "Silme başarısız");
                    }
                }}
            />
        </div>
    );
}

/* ============================================================================
 * ImageCard — Models sayfasıyla aynı yapı: thumbnail üstte, altta name +
 * metadata + separator + "N ürüne bağlı" chip + edit/delete icon buttons.
 * ============================================================================ */
const ImageCard = ({ img, onOpen, onDelete }) => (
    <div
        className="rounded-xl border border-border bg-surface overflow-hidden flex flex-col hover:border-foreground/25 transition-colors"
        data-testid={`image-card-${img.id}`}
    >
        <button
            type="button"
            onClick={onOpen}
            className="block w-full aspect-square bg-muted/20 relative overflow-hidden p-4"
            data-testid={`image-thumb-${img.id}`}
        >
            <img
                src={imageSrc(img.id, img.current_version)}
                alt={img.alt || img.name}
                className="block w-full h-full object-contain"
                loading="lazy"
            />
        </button>
        <div className="p-4 flex-1 flex flex-col">
            <button
                type="button"
                onClick={onOpen}
                className="block w-full text-left"
                data-testid={`image-name-${img.id}`}
            >
                <div className="font-display text-base font-bold tracking-tight truncate">{img.name || "Görsel"}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                    {mimeLabel(img.mime_type)}
                    {img.width && img.height ? ` · ${img.width}×${img.height}` : ""}
                    {" · "}{formatSize(img.size_bytes)}
                </div>
            </button>
            <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t border-border">
                <div className="text-[11px] text-muted-foreground inline-flex items-center gap-1.5" data-testid={`image-usage-${img.id}`}>
                    <span className="font-mono num rounded bg-muted px-1.5 py-0.5 text-foreground">{img.usageCount || 0}</span>
                    ürüne bağlı
                </div>
                <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onOpen} data-testid={`image-edit-${img.id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={onDelete}
                        data-testid={`image-delete-${img.id}`}
                    >
                        <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                </div>
            </div>
        </div>
    </div>
);

/* ============================================================================
 * UploadSheet — file picker + name + alt + preview + save (yeni görsel)
 * ============================================================================ */
const UploadSheet = ({ open, onClose, onUpload }) => {
    const [file, setFile] = useState(null);
    const [dims, setDims] = useState(null); // {width, height} | null — client-side probe
    const [name, setName] = useState("");
    const [alt, setAlt] = useState("");
    const [saving, setSaving] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const reset = useCallback(() => {
        setFile(null); setDims(null); setName(""); setAlt(""); setSaving(false); setDragOver(false);
    }, []);

    useEffect(() => {
        if (!open) reset();
    }, [open, reset]);

    const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
    useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

    const validate = (f) => {
        if (!f) return "Dosya seçilmedi";
        if (!ALLOWED_MIME.includes(f.type)) return "Desteklenmeyen dosya türü. PNG, JPG, WebP veya SVG olmalı";
        if (f.size > MAX_BYTES) return `Dosya boyutu ${formatSize(MAX_BYTES)} sınırını aşıyor (seçilen: ${formatSize(f.size)})`;
        return null;
    };

    const pickFile = async (f) => {
        const err = validate(f);
        if (err) { toast.error(err); return; }
        setFile(f);
        // Fire-and-forget dimension probe. If it succeeds we can show
        // orientation preview + narrow-width warning inline before upload.
        const d = await probeDimensions(f);
        setDims(d);
        // Auto-suggest name from filename (without extension)
        if (!name && f.name) {
            const base = f.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " ");
            setName(base.slice(0, 60));
        }
    };

    const onDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        if (f) pickFile(f);
    };

    const submit = async () => {
        if (!file) return;
        const finalName = (name || "").trim() || file.name.replace(/\.[^.]+$/, "");
        setSaving(true);
        try {
            await onUpload(file, { name: finalName, alt: alt.trim() });
            toast.success("Görsel yüklendi");
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Yükleme başarısız");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col" data-testid="images-upload-sheet">
                <SheetHeader className="p-6 pb-4 border-b border-border">
                    <SheetTitle>Yeni Görsel Ekle</SheetTitle>
                    <SheetDescription>PNG, JPG, WebP veya SVG · Max {formatSize(MAX_BYTES)}</SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto p-6 space-y-5">
                    {/* File picker / drop zone */}
                    {!file ? (
                        <div
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={() => inputRef.current?.click()}
                            className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${dragOver ? "border-foreground bg-muted/40" : "border-border hover:border-foreground/40 bg-muted/20"}`}
                            data-testid="images-upload-dropzone"
                        >
                            <div className="mx-auto h-12 w-12 rounded-lg border border-border bg-background grid place-items-center mb-3">
                                <Upload className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                            </div>
                            <p className="text-sm font-medium">Görseli buraya sürükle</p>
                            <p className="text-xs text-muted-foreground mt-1">veya <span className="underline">tıklayıp seç</span></p>
                            <input
                                ref={inputRef}
                                type="file"
                                accept={ALLOWED_MIME.join(",")}
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && pickFile(e.target.files[0])}
                                data-testid="images-upload-input"
                            />
                        </div>
                    ) : (
                        <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3" data-testid="images-upload-preview">
                            <div className="relative w-full aspect-square rounded-lg bg-background border border-border overflow-hidden p-3">
                                <img src={previewUrl} alt="preview" className="block w-full h-full object-contain" />
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-xs text-muted-foreground truncate">
                                    <div className="font-medium text-foreground truncate">{file.name}</div>
                                    {formatSize(file.size)}
                                    {dims?.width && dims?.height ? ` · ${dims.width}×${dims.height}px` : ""}
                                </div>
                                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => { setFile(null); setDims(null); }} data-testid="images-upload-clear">
                                    <X className="h-3 w-3 mr-1" /> Değiştir
                                </Button>
                            </div>
                            {/* Soft warning: image narrower than the recommended min. Merchant
                                can still upload — some SVG diagrams are intentionally small. */}
                            {dims?.width && dims.width < MIN_RECOMMENDED_WIDTH && file.type !== "image/svg+xml" && (
                                <div
                                    className="flex items-start gap-2 rounded-lg border border-amber-400/40 bg-amber-50 p-2.5 text-[11px] text-amber-900"
                                    data-testid="images-upload-warn-small"
                                >
                                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" strokeWidth={1.75} />
                                    <span>
                                        Görsel <strong>{dims.width}px</strong> genişliğinde — en az{" "}
                                        <strong>{MIN_RECOMMENDED_WIDTH}px</strong> öneririz. Widget&apos;ta retina ekranlarda bulanık görünebilir.
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Name */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">İsim</label>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="ör. Çanta Ölçüm Diyagramı"
                            maxLength={80}
                            data-testid="images-upload-name"
                        />
                        <div className="text-[10px] text-muted-foreground/70 flex justify-between">
                            <span>Tablolarda görsel seçerken bu isim görünür</span>
                            <span>{name.length}/80</span>
                        </div>
                    </div>

                    {/* Alt */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Alt Metin (opsiyonel)</label>
                        <Textarea
                            value={alt}
                            onChange={(e) => setAlt(e.target.value)}
                            placeholder="Ekran okuyucular için kısa açıklama"
                            maxLength={200}
                            rows={2}
                            data-testid="images-upload-alt"
                        />
                        <div className="text-[10px] text-muted-foreground/70 text-right">{alt.length}/200</div>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-border p-4 flex items-center justify-end gap-2">
                    <Button variant="outline" onClick={onClose} disabled={saving} data-testid="images-upload-cancel">
                        Vazgeç
                    </Button>
                    <Button onClick={submit} disabled={!file || saving} data-testid="images-upload-save">
                        {saving ? (
                            <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Yükleniyor...</>
                        ) : (
                            <><Upload className="h-4 w-4 mr-1.5" /> Yükle</>
                        )}
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

/* ============================================================================
 * ImageDetailSheet — preview + name/alt + version history + rollback
 * ----------------------------------------------------------------------------
 * "Yeni versiyon" = aynı image slot'a farklı byte'lar. `sizingImageId`
 * bağlantısı olan HERKES anında yeni versiyonu görür. Eski byte'lar
 * `history` içinde saklanır; istediğin an geri dönebilirsin.
 * Uyarı için "N ürünü etkiler" bilgisi gösterilir; onay değil, farkındalık.
 * ============================================================================ */
const ImageDetailSheet = ({ image, onClose, onRename, onUploadVersion, onRollback, onDelete }) => {
    const [name, setName] = useState("");
    const [alt, setAlt] = useState("");
    const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
    const [uploadingVersion, setUploadingVersion] = useState(false);
    const [rollingBackTo, setRollingBackTo] = useState(null); // version being rolled back
    const versionInputRef = useRef(null);
    // Track what we last persisted so blur handlers can no-op when nothing
    // changed and skip the roundtrip. Refs (not state) because these values
    // never drive rendering.
    const nameLastSavedRef = useRef("");
    const altLastSavedRef = useRef("");
    const savedTimerRef = useRef(null);

    useEffect(() => {
        if (image) {
            setName(image.name || "");
            setAlt(image.alt || "");
            nameLastSavedRef.current = image.name || "";
            altLastSavedRef.current = image.alt || "";
            setSaveStatus("idle");
        }
    }, [image?.id]);

    // Flash "Kaydedildi ✓" for 2s after a successful save then fade to idle.
    useEffect(() => {
        if (saveStatus !== "saved") return undefined;
        savedTimerRef.current = setTimeout(() => setSaveStatus("idle"), 2000);
        return () => clearTimeout(savedTimerRef.current);
    }, [saveStatus]);

    if (!image) return null;

    const history = image.history || [];
    const currentVer = image.current_version || 1;
    const olderVersions = history.filter((v) => v.version !== currentVer).sort((a, b) => b.version - a.version);
    const usageCount = image.usageCount || 0;

    // Auto-save on blur. Guards: dirty check + non-empty name validation.
    // Name required; if the merchant clears it we quietly revert to last
    // saved and surface a toast instead of persisting an invalid empty state.
    const saveMeta = async () => {
        const trimmedName = name.trim();
        const trimmedAlt = alt.trim();
        if (trimmedName === nameLastSavedRef.current && trimmedAlt === altLastSavedRef.current) return;
        if (!trimmedName) {
            setName(nameLastSavedRef.current);
            toast.error("İsim boş olamaz");
            return;
        }
        setSaveStatus("saving");
        try {
            await onRename(image.id, { name: trimmedName, alt: trimmedAlt });
            nameLastSavedRef.current = trimmedName;
            altLastSavedRef.current = trimmedAlt;
            setSaveStatus("saved");
        } catch (e) {
            setSaveStatus("error");
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        }
    };

    const pickVersionFile = () => versionInputRef.current?.click();

    const handleVersionFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        // Reset input so the same file can be picked twice in a row.
        e.target.value = "";
        if (!ALLOWED_MIME.includes(f.type)) {
            toast.error("Desteklenmeyen dosya türü. PNG, JPG, WebP veya SVG olmalı");
            return;
        }
        if (f.size > MAX_BYTES) {
            toast.error(`Dosya boyutu ${formatSize(MAX_BYTES)} sınırını aşıyor`);
            return;
        }
        // Soft warning for narrow raster images (SVG scales losslessly so we
        // exempt it). Warning only — merchant can proceed if they want.
        if (f.type !== "image/svg+xml") {
            const d = await probeDimensions(f);
            if (d.width && d.width < MIN_RECOMMENDED_WIDTH) {
                toast.warning(`Görsel ${d.width}px genişliğinde — en az ${MIN_RECOMMENDED_WIDTH}px öneririz`, {
                    description: "Widget'ta retina ekranlarda bulanık görünebilir. Yine de yüklendi.",
                });
            }
        }
        setUploadingVersion(true);
        try {
            await onUploadVersion(image.id, f);
            const msg = usageCount > 0
                ? `Yeni versiyon yüklendi · ${usageCount} ürüne otomatik yansıdı`
                : "Yeni versiyon yüklendi";
            toast.success(msg);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Versiyon yüklenemedi");
        } finally {
            setUploadingVersion(false);
        }
    };

    const handleRollback = async (version) => {
        setRollingBackTo(version);
        try {
            await onRollback(image.id, version);
            const msg = usageCount > 0
                ? `Versiyon ${version} aktif · ${usageCount} ürüne yansıdı`
                : `Versiyon ${version} aktif`;
            toast.success(msg);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Geri dönüş başarısız");
        } finally {
            setRollingBackTo(null);
        }
    };

    return (
        <Sheet open={!!image} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col overflow-hidden" data-testid="images-detail-sheet">
                <SheetHeader className="px-6 py-5 border-b border-border">
                    <SheetTitle className="font-display text-lg font-bold tracking-tight">Görsel Detayı</SheetTitle>
                    <SheetDescription className="sr-only">
                        Görsel bilgilerini düzenle, yeni versiyon yükle veya eski bir versiyona dön.
                    </SheetDescription>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto scroll-thin">
                    {/* Section 1 — Preview + meta chips
                     *
                     * Pattern: sabit `aspect-square` container + `p-6` iç
                     * padding + img `w-full h-full object-contain block`.
                     * Container her zaman kare (sheet genişliğine göre); img
                     * padding'li kutunun içine oran orantılı yerleşir.
                     * `overflow-hidden` upscale edilen küçük görsellerin
                     * padding'i taşımasına engel olur.
                     */}
                    <div className="px-6 py-6 space-y-4 border-b border-border/60">
                        <div className="relative w-full aspect-square rounded-xl border border-border bg-muted/20 overflow-hidden p-6">
                            <img
                                src={imageSrc(image.id, currentVer)}
                                alt={image.alt || image.name}
                                className="block w-full h-full object-contain"
                                data-testid="images-detail-preview"
                            />
                            {uploadingVersion && (
                                <div className="absolute inset-0 bg-background/70 backdrop-blur-sm grid place-items-center" data-testid="images-detail-uploading">
                                    <div className="flex flex-col items-center gap-2 text-sm">
                                        <Loader2 className="h-6 w-6 animate-spin" />
                                        <span>Yeni versiyon yükleniyor…</span>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Meta chip row: version + dimensions + usage */}
                        <div className="flex flex-wrap items-center gap-2" data-testid="images-detail-meta">
                            <span className="inline-flex items-center rounded-md bg-foreground text-background px-1.5 py-0.5 text-[10px] font-semibold font-mono num" data-testid="images-detail-version-badge">
                                v{currentVer}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                                {mimeLabel(image.mime_type)}
                                {image.width && image.height ? ` · ${image.width}×${image.height}px` : ""}
                                {" · "}{formatSize(image.size_bytes)}
                            </span>
                            <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="images-detail-usage">
                                <LinkIcon className="h-3 w-3" strokeWidth={1.75} />
                                <span className="font-mono num rounded bg-muted px-1.5 py-0.5 text-foreground">{usageCount}</span>
                                ürüne bağlı
                            </span>
                        </div>
                    </div>

                    {/* Section 2 — Görseli güncelle (prominent action) */}
                    <div className="px-6 py-6 space-y-2 border-b border-border/60">
                        <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground mb-2">
                            Görseli Güncelle
                        </div>
                        <Button
                            variant="outline"
                            className="w-full h-10"
                            onClick={pickVersionFile}
                            disabled={uploadingVersion}
                            data-testid="images-detail-new-version-btn"
                        >
                            <Upload className="h-4 w-4 mr-2" strokeWidth={1.75} />
                            Yeni Versiyon Yükle
                        </Button>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                            {usageCount > 0
                                ? <>Yeni dosya <strong className="text-foreground">{usageCount} ürüne</strong> anında yansır. Eski versiyon geçmişte kalır, geri dönebilirsin.</>
                                : "Eski versiyon geçmişte kalır, geri dönebilirsin."}
                        </p>
                        <input
                            ref={versionInputRef}
                            type="file"
                            accept={ALLOWED_MIME.join(",")}
                            className="hidden"
                            onChange={handleVersionFile}
                            data-testid="images-detail-new-version-input"
                        />
                    </div>

                    {/* Section 3 — Bilgiler (auto-save on blur) */}
                    <div className="px-6 py-6 space-y-4 border-b border-border/60">
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                                Bilgiler
                            </div>
                            <SaveStatus status={saveStatus} />
                        </div>
                        <div className="space-y-1.5">
                            <label htmlFor="img-name" className="text-[11px] font-medium text-muted-foreground">İsim</label>
                            <Input
                                id="img-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                onBlur={saveMeta}
                                maxLength={80}
                                data-testid="images-detail-name"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label htmlFor="img-alt" className="text-[11px] font-medium text-muted-foreground">Alt Metin</label>
                            <Textarea
                                id="img-alt"
                                value={alt}
                                onChange={(e) => setAlt(e.target.value)}
                                onBlur={saveMeta}
                                maxLength={200}
                                rows={2}
                                placeholder="Ekran okuyucular için kısa açıklama"
                                data-testid="images-detail-alt"
                            />
                        </div>
                        <p className="text-[10px] text-muted-foreground/80 italic">
                            Değişiklikler otomatik olarak kaydedilir.
                        </p>
                    </div>

                    {/* Section 4 — Version history (rollback) */}
                    {olderVersions.length > 0 && (
                        <div className="px-6 py-6 space-y-3" data-testid="images-detail-history">
                            <div className="flex items-center gap-2">
                                <History className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                                <h4 className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">
                                    Eski Versiyonlar
                                </h4>
                                <span className="text-[10px] text-muted-foreground/70 font-mono num">
                                    ({olderVersions.length})
                                </span>
                            </div>
                            <div className="space-y-2">
                                {olderVersions.map((v) => (
                                    <div
                                        key={v.version}
                                        className="rounded-lg border border-border bg-muted/10 p-3 flex items-center gap-3 hover:border-foreground/20 transition-colors"
                                        data-testid={`images-version-${v.version}`}
                                    >
                                        <div className="h-14 w-14 rounded-md border border-border bg-background overflow-hidden shrink-0 p-1.5">
                                            <img
                                                src={imageSrc(image.id, v.version)}
                                                alt={`v${v.version}`}
                                                className="block w-full h-full object-contain"
                                                loading="lazy"
                                            />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono num font-semibold">
                                                v{v.version}
                                            </span>
                                            <div className="text-[11px] text-muted-foreground truncate mt-1">
                                                {mimeLabel(v.mime_type)}
                                                {v.width && v.height ? ` · ${v.width}×${v.height}` : ""}
                                                {" · "}{formatSize(v.size_bytes)}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground/70 truncate">
                                                {fmtDate(v.uploaded_at)}
                                            </div>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-8 px-2.5 text-[11px] shrink-0"
                                            onClick={() => handleRollback(v.version)}
                                            disabled={rollingBackTo !== null || uploadingVersion}
                                            data-testid={`images-rollback-${v.version}`}
                                        >
                                            {rollingBackTo === v.version ? (
                                                <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Dönülüyor</>
                                            ) : (
                                                <><RotateCcw className="h-3 w-3 mr-1" /> Geri dön</>
                                            )}
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer — sadece Sil ve Kapat. Kaydet YOK (auto-save). */}
                <div className="border-t border-border px-4 py-3 flex items-center justify-between gap-2 bg-muted/10">
                    <Button
                        variant="ghost"
                        onClick={() => onDelete(image)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="images-detail-delete"
                    >
                        <Trash2 className="h-4 w-4 mr-1.5" /> Sil
                    </Button>
                    <Button variant="outline" onClick={onClose} data-testid="images-detail-cancel">
                        Kapat
                    </Button>
                </div>
            </SheetContent>
        </Sheet>
    );
};

/* Save status pill — flashes "Kaydedildi ✓" for 2 seconds then fades. */
const SaveStatus = ({ status }) => {
    if (status === "saving") {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="images-detail-saving">
                <Loader2 className="h-3 w-3 animate-spin" /> Kaydediliyor…
            </span>
        );
    }
    if (status === "saved") {
        return (
            <span className="inline-flex items-center gap-1 text-[10px] text-emerald-600" data-testid="images-detail-saved">
                <Check className="h-3 w-3" /> Kaydedildi
            </span>
        );
    }
    return null;
};

/* ============================================================================
 * ImagesDeleteDialog — thin wrapper over the shared DeleteConfirmDialog.
 * Computes affected products live so the count stays accurate even if some
 * products got reassigned between the grid fetch and the delete confirm.
 * ============================================================================ */
const ImagesDeleteDialog = ({ image, products, onCancel, onConfirm }) => {
    const affectedCount = useMemo(() => {
        if (!image) return 0;
        return (products || []).filter((p) => p.sizingImageId === image.id).length;
    }, [image, products]);

    if (!image) return null;

    return (
        <DeleteConfirmDialog
            open={!!image}
            onOpenChange={(o) => !o && onCancel()}
            itemName={image.name}
            impactCount={affectedCount}
            impactMessage={
                <>
                    <strong>{affectedCount} ürün</strong> bu görseli kullanıyor. Silersen bu ürünler görselsiz kalır ve widget&apos;ta beden tablosu görseli görünmez.
                </>
            }
            permanentNote={affectedCount > 0
                ? "Görsel dosyası ve tüm versiyon geçmişi kalıcı olarak silinir. Bu işlem geri alınamaz."
                : "Görsel ve tüm versiyon geçmişi kalıcı olarak silinir. Bu işlem geri alınamaz."}
            ackMessage={<><strong>{affectedCount} ürün</strong> bu silme sonrası görselsiz kalacak.</>}
            onConfirm={onConfirm}
            testId="images-delete"
        />
    );
};
